import * as THREE from 'three';
import { Enemy, type DropTableEntry } from './Enemy.js';
import type { Game } from '../../game/Game.js';
import type { Circle } from '../../systems/Collision.js';
import { Projectile } from '../Projectile.js';
import { Runner } from './Runner.js';
import { audio } from '../../audio/Audio.js';

/**
 * Boss drop table.
 *
 * Always drops high-tier parts:
 * - Tier 1 parts (Analyst, Railgun, Dynamo, Strider)
 * - Higher chance of multiple drops
 * - No "null" drops - bosses always drop loot
 */
const BOSS_DROPS: DropTableEntry[] = [
  { partId: 'analyst', weight: 10 },    // Tier 1 head
  { partId: 'railgun', weight: 10 },    // Tier 1 weapon
  { partId: 'dynamo', weight: 10 },     // Tier 1 core
  { partId: 'strider', weight: 10 },    // Tier 1 transport
];

/**
 * Boss enemy - large, multi-phase enemy that appears every third floor.
 *
 * Behavior:
 * - Three phases based on HP thresholds
 * - Phase 1 (100-60% HP): Slow aimed shots, occasional summons
 * - Phase 2 (60-30% HP): Faster fire rate, spread shots, more summons
 * - Phase 3 (30-0% HP): Rapid fire, movement, desperate attacks
 *
 * Visual:
 * - 2x normal enemy size
 * - Darker red, more menacing color
 * - Large imposing structure
 * - Size: ~2.4 units (double normal enemies)
 *
 * Stats:
 * - HP: 200 base + 100 per floor (200 on F3, 300 on F6, 400 on F9, etc.)
 * - Collision radius: 1.2 (larger hitbox)
 * - Multiple attack patterns per phase
 */
export class Boss extends Enemy {
  /** Current combat phase (1-3) */
  private phase: number = 1;

  /** Current floor number (affects HP scaling) */
  private floor: number;

  /** Time until next shot can be fired (seconds) */
  private fireTimer: number = 0;

  /** Time until next summon (seconds) */
  private summonTimer: number = 0;

  /** Projectile speed in units per second */
  private readonly projectileSpeed: number = 9;

  /** Damage dealt per projectile */
  private readonly baseDamage: number = 25;

  /** Collision radius for hitbox */
  private readonly collisionRadius: number = 1.2;

  /** Detection range */
  private readonly detectionRadius: number = 15;

  /** Movement speed (only in phase 3) */
  private readonly speed: number = 2.0;

  /** Telegraph duration before firing (seconds) */
  private readonly telegraphDuration: number = 0.4;

  /** Whether ready to fire after telegraph */
  private readyToFire: boolean = false;

  /** Reference to the main body mesh for special effects */
  private bodyMesh: THREE.Mesh | null = null;

  /** Reference to weapon barrels */
  private barrels: THREE.Mesh[] = [];

  constructor(x: number, y: number, game: Game, floor: number) {
    // Calculate HP based on floor: 200 base + 100 per floor
    const hp = 200 + (floor - 1) * 100;
    super(x, y, hp, BOSS_DROPS, game);

    this.floor = floor;
    this.createMesh();
  }

  /**
   * Create the boss's visual mesh.
   *
   * Visual structure:
   * - Large imposing body (2x normal enemy size)
   * - Multiple weapon barrels
   * - Darker, more menacing color scheme
   */
  private createMesh(): void {
    const group = new THREE.Group();

    // Main body - large sphere for imposing presence
    const bodyGeometry = new THREE.SphereGeometry(1.2, 32, 32);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xb71c1c, // Dark red - more menacing than regular enemies
      metalness: 0.7,
      roughness: 0.4,
      emissive: 0x330000, // Slight red glow
      emissiveIntensity: 0.3,
    });
    this.bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.bodyMesh.position.y = 0.6; // Slightly elevated
    group.add(this.bodyMesh);

    // Armor plating - darker accents
    const plateGeometry = new THREE.TorusGeometry(1.0, 0.15, 8, 16);
    const plateMaterial = new THREE.MeshStandardMaterial({
      color: 0x7f0000, // Very dark red
      metalness: 0.8,
      roughness: 0.3,
    });

    // Three rings at different heights
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(plateGeometry, plateMaterial);
      ring.position.y = 0.3 + i * 0.3;
      ring.rotation.x = Math.PI / 2;
      group.add(ring);
    }

    // Weapon barrels - four barrels pointing in cardinal directions
    const barrelGeometry = new THREE.CylinderGeometry(0.12, 0.15, 1.2, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: 0x424242, // Dark gray
      emissive: 0x000000, // Start with no emissive
      metalness: 0.8,
      roughness: 0.3,
    });

    // Create 4 barrels around the body
    for (let i = 0; i < 4; i++) {
      const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial.clone());
      barrel.rotation.z = Math.PI / 2; // Point horizontally
      const angle = (Math.PI / 2) * i;
      barrel.position.set(
        Math.cos(angle) * 1.1,
        0.6,
        Math.sin(angle) * 1.1
      );
      this.barrels.push(barrel);
      group.add(barrel);
    }

    this.mesh = group;
    this.updateMeshTransform();
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // Update telegraph animation
    const telegraphComplete = this.updateTelegraph(dtSec);
    if (telegraphComplete) {
      this.readyToFire = true;
    }

    // Update barrel glow during telegraph
    if (this.isTelegraphing) {
      const t = this.telegraphTimer / this.telegraphDuration;
      const glowIntensity = (1 - t) * 3; // Intensify as we get closer to firing
      for (const barrel of this.barrels) {
        const material = barrel.material as THREE.MeshStandardMaterial;
        material.emissive.setHex(0xff0000); // Red glow
        material.emissiveIntensity = glowIntensity;
      }
    } else {
      // Reset barrel glow
      for (const barrel of this.barrels) {
        const material = barrel.material as THREE.MeshStandardMaterial;
        material.emissive.setHex(0x000000);
        material.emissiveIntensity = 0;
      }
    }

    // Check for phase transitions
    this.checkPhaseTransition();

    // Update phase-specific visuals
    this.updatePhaseVisuals();

    // Decrement timers
    if (this.fireTimer > 0) {
      this.fireTimer -= dtSec;
    }
    if (this.summonTimer > 0) {
      this.summonTimer -= dtSec;
    }

    // Find the player
    const player = this.findPlayer();
    if (!player) {
      return;
    }

    // Calculate distance to player
    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Always face the player
    this.rotation = Math.atan2(dy, dx);

    // Check if player is in detection range
    if (dist <= this.detectionRadius) {
      // Execute phase-specific behavior
      this.executePattern(dtSec, player.position.x, player.position.y, dist);
    }

    this.updateMeshTransform();
  }

  /**
   * Check for phase transitions based on HP thresholds.
   */
  private checkPhaseTransition(): void {
    const hpPercent = this.hp / this.maxHP;

    if (hpPercent <= 0.3 && this.phase < 3) {
      this.phase = 3;
      console.log('Boss entered Phase 3 (Desperate)!');
      this.onPhaseChange();
    } else if (hpPercent <= 0.6 && this.phase < 2) {
      this.phase = 2;
      console.log('Boss entered Phase 2 (Aggressive)!');
      this.onPhaseChange();
    }
  }

  /**
   * Called when boss transitions to a new phase.
   * Can add visual/audio feedback here.
   */
  private onPhaseChange(): void {
    // Play boss phase change sound
    audio.bossPhase();

    // Trigger screen shake
    this.game.shake(0.5);

    // Reset timers to immediate action
    this.fireTimer = 0;
    this.summonTimer = 0;
  }

  /**
   * Update visual effects based on current phase.
   */
  private updatePhaseVisuals(): void {
    if (!this.bodyMesh) return;

    const material = this.bodyMesh.material as THREE.MeshStandardMaterial;

    switch (this.phase) {
      case 1:
        // Normal red glow
        material.emissiveIntensity = 0.3;
        break;
      case 2:
        // Increased glow
        material.emissiveIntensity = 0.6;
        break;
      case 3:
        // Maximum glow + pulsing
        const pulse = Math.sin(Date.now() * 0.01) * 0.3 + 0.7; // 0.4 to 1.0
        material.emissiveIntensity = pulse;
        break;
    }
  }

  /**
   * Execute attack pattern based on current phase.
   */
  private executePattern(dtSec: number, targetX: number, targetY: number, dist: number): void {
    switch (this.phase) {
      case 1:
        this.phase1Behavior(targetX, targetY);
        break;
      case 2:
        this.phase2Behavior(targetX, targetY);
        break;
      case 3:
        this.phase3Behavior(dtSec, targetX, targetY, dist);
        break;
    }
  }

  /**
   * Phase 1 behavior: Slow aimed shots, occasional summons.
   */
  private phase1Behavior(targetX: number, targetY: number): void {
    // Slow fire rate: 0.5 shots per second
    const fireRate = 0.5;

    // Start telegraph when ready to shoot
    if (this.fireTimer <= 0 && !this.isTelegraphing && !this.readyToFire) {
      this.startTelegraph(this.telegraphDuration);
    }

    // Fire when telegraph completes
    if (this.readyToFire) {
      this.fireAimedShot(targetX, targetY);
      this.fireTimer = 1 / fireRate;
      this.readyToFire = false;
    }

    // Summon a runner every 5 seconds
    if (this.summonTimer <= 0) {
      this.summonEnemy();
      this.summonTimer = 5.0;
    }
  }

  /**
   * Phase 2 behavior: Faster fire rate, spread shots, more summons.
   */
  private phase2Behavior(targetX: number, targetY: number): void {
    // Faster fire rate: 1 shot per second
    const fireRate = 1.0;

    // Start telegraph when ready to shoot
    if (this.fireTimer <= 0 && !this.isTelegraphing && !this.readyToFire) {
      this.startTelegraph(this.telegraphDuration * 0.8); // Shorter telegraph
    }

    // Fire spread shot when telegraph completes
    if (this.readyToFire) {
      this.fireSpreadShot(targetX, targetY);
      this.fireTimer = 1 / fireRate;
      this.readyToFire = false;
    }

    // Summon more frequently: every 3 seconds
    if (this.summonTimer <= 0) {
      this.summonEnemy();
      this.summonTimer = 3.0;
    }
  }

  /**
   * Phase 3 behavior: Rapid fire, movement toward player, desperate attacks.
   */
  private phase3Behavior(dtSec: number, targetX: number, targetY: number, dist: number): void {
    // Very fast fire rate: 2 shots per second
    const fireRate = 2.0;

    // Start telegraph when ready to shoot
    if (this.fireTimer <= 0 && !this.isTelegraphing && !this.readyToFire) {
      this.startTelegraph(this.telegraphDuration * 0.6); // Even shorter telegraph
    }

    // Fire burst shot when telegraph completes
    if (this.readyToFire) {
      this.fireBurstShot(targetX, targetY);
      this.fireTimer = 1 / fireRate;
      this.readyToFire = false;
    }

    // Move toward player aggressively
    if (dist > 5) { // Don't get too close
      const dx = targetX - this.position.x;
      const dy = targetY - this.position.y;
      const ndx = dx / dist;
      const ndy = dy / dist;

      this.position.x += ndx * this.speed * dtSec;
      this.position.y += ndy * this.speed * dtSec;

      // Clamp to room bounds
      const room = this.game.getRoom();
      this.position = room.clampPosition(this.position, this.collisionRadius);
    }

    // Summon very frequently: every 2 seconds
    if (this.summonTimer <= 0) {
      this.summonEnemy();
      this.summonTimer = 2.0;
    }
  }

  /**
   * Fire a single aimed shot at the target.
   */
  private fireAimedShot(targetX: number, targetY: number): void {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const angle = Math.atan2(dy, dx);

    this.createProjectile(angle, this.baseDamage);
  }

  /**
   * Fire a spread of 3 shots.
   */
  private fireSpreadShot(targetX: number, targetY: number): void {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const baseAngle = Math.atan2(dy, dx);

    // Fire 3 shots in a spread pattern
    const spreadAngle = Math.PI / 8; // 22.5 degrees
    for (let i = -1; i <= 1; i++) {
      const angle = baseAngle + i * spreadAngle;
      this.createProjectile(angle, this.baseDamage);
    }
  }

  /**
   * Fire a burst of 5 shots in all directions.
   */
  private fireBurstShot(targetX: number, targetY: number): void {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const baseAngle = Math.atan2(dy, dx);

    // Fire 5 shots in a wider spread
    const spreadAngle = Math.PI / 6; // 30 degrees
    for (let i = -2; i <= 2; i++) {
      const angle = baseAngle + i * spreadAngle;
      this.createProjectile(angle, this.baseDamage * 0.8); // Slightly less damage per shot
    }
  }

  /**
   * Create a projectile at the given angle.
   */
  private createProjectile(angle: number, damage: number): void {
    const projectile = new Projectile(
      this.position.x,
      this.position.y,
      angle,
      this.projectileSpeed,
      damage,
      4 // Projectile lifetime in seconds
    );

    // Set owner to prevent self-collision
    projectile.setOwner(this);

    // Set room for wall collision
    const room = this.game.getRoom();
    projectile.setRoom(room);

    // Change projectile color to dark red for boss projectiles
    if (projectile.mesh instanceof THREE.Mesh) {
      const material = projectile.mesh.material as THREE.MeshStandardMaterial;
      material.color.setHex(0xb71c1c); // Dark red
      material.emissive.setHex(0xb71c1c);
      material.emissiveIntensity = 0.8; // Brighter glow
    }

    // Set impact effect callback
    projectile.setOnDestroy((x, y) => {
      this.game.spawnImpactEffect(x, y, 0xb71c1c); // Dark red impact
    });

    this.game.addEntity(projectile);
  }

  /**
   * Summon a Runner enemy near the boss.
   */
  private summonEnemy(): void {
    // Spawn runner at random offset from boss
    const angle = Math.random() * Math.PI * 2;
    const distance = 3; // Spawn 3 units away
    const x = this.position.x + Math.cos(angle) * distance;
    const y = this.position.y + Math.sin(angle) * distance;

    // Clamp to room bounds (use runner's collision radius of 0.3)
    const room = this.game.getRoom();
    const clampedPos = room.clampPosition({ x, y }, 0.3);

    this.game.spawnRunner(clampedPos.x, clampedPos.y);

    // Visual feedback
    this.game.spawnImpactEffect(clampedPos.x, clampedPos.y, 0xff5722);
  }

  /**
   * Find the player entity in the game.
   */
  private findPlayer(): { position: { x: number; y: number } } | null {
    return (this.game as any).playerMech || null;
  }

  /**
   * Override death to handle boss-specific loot drops.
   * Bosses drop multiple items.
   */
  protected onDeath(): void {
    // Play boss defeated sound (override the normal enemy death sound)
    audio.bossDefeated();

    // Notify game that an enemy was killed
    this.game.onEnemyKilled();

    // Boss drops 2-3 items instead of just one
    const dropCount = 2 + Math.floor(Math.random() * 2); // 2 or 3 drops

    for (let i = 0; i < dropCount; i++) {
      const drop = this.rollDrop();
      if (drop) {
        // Spread drops in a circle around the boss
        const angle = (Math.PI * 2 * i) / dropCount;
        const offset = 1.5;
        const x = this.position.x + Math.cos(angle) * offset;
        const y = this.position.y + Math.sin(angle) * offset;
        this.game.spawnPickupById(x, y, drop);
      }
    }

    // Extra visual feedback for boss death
    this.game.shake(0.8);
    this.game.spawnImpactEffect(this.position.x, this.position.y, 0xffd700); // Gold explosion
  }

  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }
}
